import React from 'react';
import { useTable, usePagination, useRowSelect, HeaderProps, Row } from 'react-table';
import {
    Checkbox, Paper, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Typography, Box, Button, Grid, Chip, IconButton,
    Select, MenuItem, CircularProgress
} from '@material-ui/core';
import MuiTable from "@material-ui/core/Table";

import { formatDateDate } from '../../../common/formatDate';
import { ThreadedViewer } from '../../../common/threadedViewer/threadedViewer';
import Pagination from '@material-ui/lab/Pagination';
import ScreenWidth from '../../../common/ScreenWidth';
import { toast } from 'react-toastify';
import { useReportBuilderFilterDownload } from '../../projectManagementMutations';
import { useFetchContext } from '../../../common/FetchContext';

// Added interface for clarity
interface FilterTableProps {
    Data: any[];
    totalCount: number;
    loading?: boolean;
    fetchData: (params: { pageIndex: number; pageSize: number }) => void;
    formValues: any;
    filters: any;
    query: any;
}

export const Filtertableview = ({
    Data = [],
    totalCount = 0,
    loading = false,
    fetchData,
    formValues,
    filters,
    query

}: FilterTableProps) => {
    const widthCurrent = ScreenWidth();
    const { authAxios } = useFetchContext();

    const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
    const [excludedIds, setExcludedIds] = React.useState<string[]>([]);
    const [isSelectAll, setIsSelectAll] = React.useState(false);


    const [noteID, setNoteID] = React.useState();
    const [open, setOpen] = React.useState(noteID ? true : false);


    const { mutateAsync: exportCSV, isLoading: exportLoading } = useReportBuilderFilterDownload();


    const columns = React.useMemo(
        () => [
            {
                Header: "Id", accessor: "id",
                Cell: ({ value }: any) => (
                    <Button variant="contained" color="primary" onClick={() => {
                        setNoteID(value);
                        setOpen(true);
                    }}>
                        {value}
                    </Button>
                )

            },
            {
                Header: "Subject", accessor: "subject",
                Cell: ({ value }: any) => (
                    <Box display="flex" flexWrap="wrap" gridGap={4} >
                        <Typography variant="body2" noWrap style={{ color: value ? 'inherit' : '#9e9e9e' }}>{value ? value : "N/A"}</Typography>
                    </Box>
                )
            },
            {
                Header: "Type", accessor: "noteTypeName",
                Cell: ({ value }: any) => (
                    <Box display="flex" flexWrap="wrap" gridGap={4} >
                        <Typography variant="body2" noWrap style={{ color: value ? 'inherit' : '#9e9e9e' }}>{value ? value : "N/A"}</Typography>
                    </Box>
                )
            },
            {
                Header: "Label",
                accessor: "labels",
                Cell: ({ value }: any) => (
                    <Box display="flex" flexWrap="wrap" gridGap={4}>
                        {value?.map((label: any) => (
                            (value ? <Chip key={label.id} label={label.name}
                                size="small" variant="outlined"
                                style={{
                                    margin: '2px',
                                    backgroundColor: label.hexColor || '#9e9e9e',
                                    color: "#ffffff"
                                }}
                            /> : "N/A")
                        ))}
                    </Box>
                )
            },

            {
                Header: "Author", accessor: "username",
                Cell: ({ value }: any) => (
                    <Box display="flex" flexWrap="wrap" gridGap={4} >
                        <Typography variant="body2" noWrap style={{ color: value ? 'inherit' : '#9e9e9e' }}>{value ? value : "N/A"}</Typography>
                    </Box>
                )
            },
            {
                Header: "Owner", accessor: "ownerName",
                Cell: ({ value }: any) => (
                    <Box display="flex" flexWrap="wrap" gridGap={4} >
                        <Typography variant="body2" noWrap style={{ color: value ? 'inherit' : '#9e9e9e' }}>{value ? value : "N/A"}</Typography>
                    </Box>
                )
            },
            {
                Header: "User",
                accessor: "taggedUsers",
                Cell: ({ value }: { value: any[] }) => (
                    <Box display="flex" flexWrap="wrap" gridGap={4}>
                        {(value ?? []).map((label: any) => (
                            value ? <Chip
                                key={label.username}
                                label={label.username}
                                style={{
                                    margin: '4px',
                                    backgroundColor: "#086708",
                                    color: "#ffffff"
                                }}

                                size="small"
                                variant="outlined"
                            /> : "N/A"
                        ))}
                    </Box>
                )
            },
            {
                Header: "Creation Date", accessor: "datetime",
                Cell: ({ cell }: any) => {
                    return (
                        <Box display="flex" flexWrap="wrap" gridGap={4} >
                            <Typography variant="body2" noWrap style={{ color: cell.value ? 'inherit' : '#9e9e9e' }}>{formatDateDate(cell.value ? cell.value : "N/A")}</Typography>
                        </Box>
                    );
                }
            },
            {
                Header: "Project", accessor: "project",
                Cell: ({ value }: any) => (
                    <Box display="flex" flexWrap="wrap" gridGap={4} >
                        <Typography variant="body2" noWrap style={{ color: value ? 'inherit' : '#9e9e9e' }}>{value ? value : "N/A"}</Typography>
                    </Box>
                )
            },
            {
                Header: "Status",
                accessor: "dispositionComplete",
                Cell: ({ value }: any) => (
                    <Typography
                        variant="body2"
                        style={{
                            fontWeight: 600,
                            color: value ? "#000000" : "#0b0b0b"
                        }}
                    >
                        {value ? "Closed" : "Open"}
                    </Typography>
                )
            },
        ],
        [selectedIds, excludedIds, isSelectAll]
    );



    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        gotoPage,
        setPageSize,
        canPreviousPage,
        canNextPage,
        pageOptions,
        nextPage,
        previousPage,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data: Data,
            initialState: { pageIndex: 0, pageSize: 10 },
            manualPagination: true,
            pageCount: Math.ceil(totalCount / 10),
        },
        usePagination,

        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                {
                    id: "selection",
                    Header: () => {
                        const allPageIds = page.map((r) => (r.original as any)?.id);

                        const isAllChecked =
                            isSelectAll ||
                            (allPageIds.length > 0 &&
                                allPageIds.every((id) => selectedIds.includes(id)));

                        const isIndeterminate =
                            (!isSelectAll &&
                                allPageIds.some((id) => selectedIds.includes(id)) &&
                                !isAllChecked) ||
                            (isSelectAll &&
                                excludedIds.some((id) => allPageIds.includes(id)));

                        return (
                            <Checkbox
                                indeterminate={isIndeterminate}
                                checked={isAllChecked}
                                onChange={(e) => {
                                    const checked = e.target.checked;

                                    setIsSelectAll(checked);
                                    setSelectedIds([]);
                                    setExcludedIds([]);
                                }}
                            />
                        );
                    },



                    Cell: ({ row }: { row: Row<any> }) => {
                        const id = (row.original as any)?.id;

                        const checked = isSelectAll
                            ? !excludedIds.includes(id)
                            : selectedIds.includes(id);

                        return (
                            <Checkbox

                                checked={checked}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;

                                    if (isSelectAll) {
                                        setExcludedIds((prev) =>
                                            isChecked
                                                ? prev.filter((x) => x !== id)
                                                : [...prev, id]
                                        );
                                    } else {
                                        setSelectedIds((prev) =>
                                            isChecked
                                                ? [...prev, id]
                                                : prev.filter((x) => x !== id)
                                        );
                                    }
                                }}
                            />
                        );
                    },




                },
                ...columns,
            ]);
        }
    );

    const formatToGraphDate = (date: any) => {
        if (!date) return null;
        const d = new Date(date);
        return d.toISOString().split('T')[0];
    };

    // Sync state changes back to the parent to trigger GraphQL refetch
    React.useEffect(() => {
        fetchData({ pageIndex, pageSize });
    }, [fetchData, pageIndex, pageSize]);


    const buildExportFilters = (filters: any, values: any) => {
        const rawFilters = {
            startDate: values.startDate
                ? formatToGraphDate(values.startDate)
                : null,

            endDate: values.endDate
                ? formatToGraphDate(values.endDate)
                : null,

            creationDate: values.creationDate
                ? formatToGraphDate(values.creationDate)
                : null,

            id: filters.id ? Number(filters.id) : null,

            subject: filters.subject?.length ? filters.subject : null,

            noteType: filters.type?.length
                ? filters.type.map((t: any) => t.name)
                : null,

            labels: filters.label?.length
                ? filters.label.map((l: any) => l.name)
                : null,

            author: filters.author?.length ? filters.author : null,

            owner: filters.owner?.length ? filters.owner : null,

            taggedUsers: filters.user?.length
                ? filters.user.map((u: any) => u.email)
                : null,

            projectNumber:
                filters.projectNumber?.trim()
                    ? filters.projectNumber.trim()
                    : null,

            status: filters.status?.length
                ? filters.status.map((s: any) => (s === "Open" ? "false" : "true"))
                : null,
        };

        return Object.fromEntries(
            Object.entries(rawFilters).filter(([_, v]) => v !== null)
        );
    };



    const exportToCSV = async () => {
        if (!Data.length) return;
        const headers = ["Id", "Subject", "Type", "Labels", "Author", "Owner", "User", "Status"];

        const payload = isSelectAll
            ? {
                authAxios,
                select_all: "true",
                note_ids: excludedIds,




                filters: buildExportFilters(filters, formValues),

            }
            : {
                authAxios,
                select_all: "false",
                note_ids: selectedIds,
                filters: buildExportFilters(filters, formValues),


            };

        try {
            const response = await exportCSV(payload);
            const url = window.URL.createObjectURL(
                new Blob([response], { type: "text/csv;charset=utf-8;" })
            );

            const now = new Date();
            const formattedDateTime = now
                .toISOString()
                .replace(/T/, "_")     
                .replace(/:/g, "-")    
                .split(".")[0];        

            const fileName = `report_builder${formattedDateTime}.csv`;
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success("CSV exported successfully");


            if (response?.downloadUrl) {
                window.open(response.downloadUrl, "_blank");
            }

        } catch (error) {
            console.error("Export CSV error:", error);
            toast.error("Error exporting CSV");
        }

        // console.log("Form Values:", formValues);
        // console.log("Filters:", filters);
        console.log("Exporting IDs to CSV:", payload);
        console.log("Exporting query:", query);



    };


    return (
        <div>
            <Box width="100%" display="block" style={{ marginTop: '20px', position: 'absolute' }}>

                {/* --- TOP UI: PAGINATION & LIMIT (LEFT) | RESULTS & EXPORT (RIGHT) --- */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gridGap={16}>
                        <Pagination
                            count={pageOptions.length}
                            page={pageIndex + 1}
                            onChange={(e, val) => gotoPage(val - 1)}
                            variant="outlined"
                            color="primary"
                            size="small"
                        />
                        <Box display="flex" alignItems="center" gridGap={18}>
                            <Typography variant="body2" style={{ marginRight: 8, color: '#666' }}>
                                Show:
                            </Typography>

                            <Select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    gotoPage(0);
                                }}
                                variant="outlined"
                                style={{ height: 32, fontSize: '0.875rem', backgroundColor: '#ffffff', minWidth: '65px' }}
                                MenuProps={{ PaperProps: { style: { backgroundColor: '#ffffff' } } }}
                                inputProps={{ style: { padding: '5px 14px' } }}
                            >
                                {[10, 50, 100]
                                    // Filter options: Only show the option if the totalCount is greater 
                                    // than the previous smaller increment. 
                                    // Example: If totalCount is 33, it shows 10 and 50, but hides 100.
                                    .filter((size, index, arr) => {
                                        if (index === 0) return true; // Always show 10
                                        return totalCount > arr[index - 1];
                                    })
                                    .map((size) => (
                                        <MenuItem key={size} value={size}>
                                            {size}
                                        </MenuItem>
                                    ))
                                }
                            </Select>

                            <Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 600 }}>
                                Total Result: <span style={{ color: '#3f51b5' }}>{totalCount}</span>
                            </Typography>
                        </Box>
                        <Box display="flex" gridGap={24} style={{ marginLeft: "20px" }}>

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={exportToCSV}
                                disabled={
                                    exportLoading || (!isSelectAll && !selectedIds.length)
                                }
                                style={{ textTransform: 'uppercase', fontWeight: 600, minWidth: 130 }}
                                startIcon={
                                    exportLoading ? <CircularProgress size={18} color="inherit" /> : null
                                }
                            >
                                {exportLoading ? "Exporting..." : "Export CSV"}
                            </Button>
                        </Box>
                    </Box>


                </Box>

                <TableContainer component={Paper} style={{
                    opacity: loading ? 0.6 : 1, maxHeight: "500px",
                    overflowX: 'auto',
                    overflowY: 'auto',
                    whiteSpace: 'nowrap',
                    maxWidth: `${widthCurrent}px`
                }}>
                    {loading && (
                        <Box position="absolute" top="50%" left="50%" style={{ transform: 'translate(-50%, -50%)', zIndex: 1 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    <MuiTable stickyHeader {...getTableProps()} size="small" style={{ minWidth: 1200 }}>
                        <TableHead>
                            {headerGroups.map((headerGroup) => (
                                <TableRow {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column) => (
                                        <TableCell {...column.getHeaderProps()} style={{ backgroundColor: '#f5f5f5', whiteSpace: 'nowrap' }}>
                                            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                                {column.render("Header")}
                                            </Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHead>
                        <TableBody {...getTableBodyProps()}>
                            {page.map((row) => {
                                prepareRow(row);
                                return (
                                    <TableRow {...row.getRowProps()}>
                                        {row.cells.map((cell) => (
                                            <TableCell {...cell.getCellProps()} style={{ whiteSpace: 'nowrap' }}>
                                                {cell.render("Cell")}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </MuiTable>
                </TableContainer>

                {/* --- BOTTOM UI: REPLICATED PAGINATION (LEFT) --- */}
                <Box display="flex" alignItems="center" justifyContent="flex-start" mt={2} p={1}>
                    <Box display="flex" alignItems="center" gridGap={16}>
                        <Pagination
                            count={pageOptions.length}
                            page={pageIndex + 1}
                            onChange={(e, val) => gotoPage(val - 1)}
                            variant="outlined"
                            color="primary"
                            size="small"
                        />
                        <Box display="flex" alignItems="center" gridGap={18}>
                            <Typography variant="body2" style={{ marginRight: 8, color: '#666' }}>
                                Show:
                            </Typography>
                            <Select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    gotoPage(0);
                                }}
                                variant="outlined"
                                style={{ height: 32, fontSize: '0.875rem', backgroundColor: '#ffffff', minWidth: '65px' }}
                                MenuProps={{ PaperProps: { style: { backgroundColor: '#ffffff' } } }}
                                inputProps={{ style: { padding: '5px 14px' } }}
                            >
                                {[10, 50, 100]
                                    // Filter options: Only show the option if the totalCount is greater 
                                    // than the previous smaller increment. 
                                    // Example: If totalCount is 33, it shows 10 and 50, but hides 100.
                                    .filter((size, index, arr) => {
                                        if (index === 0) return true; // Always show 10
                                        return totalCount > arr[index - 1];
                                    })
                                    .map((size) => (
                                        <MenuItem key={size} value={size}>
                                            {size}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                            <Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 600 }}>
                                Total Result: <span style={{ color: '#3f51b5' }}>{totalCount}</span>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <ThreadedViewer id={noteID} open={open} setOpen={setOpen} />
        </div>
    );
};